// Citation and references handling

import { getAuthHeaders } from '../api/auth.js';

function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function stripHtml(s) {
    return (s || '').replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').trim();
}

function parseCitationFormats(citationDict) {
    if (!citationDict) return null;
    const result = { title: '', authors: '', year: '', journal: '' };

    // MLA: Authors. "Title." *Journal*, vol. X, no. Y, Year, pp. Z.
    // Title is always in double quotes — most reliable extraction.
    const mla = stripHtml(citationDict.mla || '');
    if (mla) {
        const mlaTitle = mla.match(/"([^"]+)"/);
        if (mlaTitle) result.title = mlaTitle[1].replace(/\.$/, '');
        const mlaJournal = mla.match(/"\.\s+\*([^*]+)\*/);
        if (mlaJournal) result.journal = mlaJournal[1];
        const mlaYear = mla.match(/,\s+(\d{4})\s*[,\.]/);
        if (mlaYear) result.year = mlaYear[1];
        // Authors = text before the opening quote
        const mlaAuthors = mla.match(/^(.+?)\.\s+"/s);
        if (mlaAuthors) result.authors = mlaAuthors[1].trim();
    }

    // APA: Authors. (Year). Title. *Journal*, ...
    const apa = stripHtml(citationDict.apa || '');
    if (apa) {
        const withJournal = apa.match(/^(.+?)\.\s*\((\d{4})\)\.\s*(.+?)\.\s*\*([^*]+)\*/s);
        if (withJournal) {
            if (!result.authors) result.authors = withJournal[1].trim();
            if (!result.year) result.year = withJournal[2];
            if (!result.title) result.title = withJournal[3].trim();
            if (!result.journal) result.journal = withJournal[4].trim();
        } else {
            const basic = apa.match(/^(.+?)\.\s*\((\d{4})\)\.\s*(.+)/s);
            if (basic) {
                if (!result.authors) result.authors = basic[1].trim();
                if (!result.year) result.year = basic[2];
                if (!result.title) {
                    const rest = basic[3];
                    const dotIdx = rest.indexOf('. ');
                    result.title = dotIdx > -1 ? rest.slice(0, dotIdx).trim() : rest.split(',')[0].trim();
                }
            }
        }
    }

    // NLM: Authors. Title. J Abbrev. Year;Vol(Issue):pages.
    if (!result.title || !result.year) {
        const nlm = stripHtml(citationDict.nlm || '');
        if (nlm) {
            const nlmMatch = nlm.match(/^(.+?)\.\s+(.+?)\.\s+(.+?)\.\s+(\d{4})/s);
            if (nlmMatch) {
                if (!result.authors) result.authors = nlmMatch[1].trim();
                if (!result.title) result.title = nlmMatch[2].trim();
                if (!result.journal) result.journal = nlmMatch[3].trim();
                if (!result.year) result.year = nlmMatch[4];
            }
        }
    }

    return (result.title || result.authors) ? result : null;
}

// Keep for backward compat (called externally in tests)
function parseCitationApa(apa) {
    return parseCitationFormats({ apa });
}

function buildCitationMeta(citation) {
    const meta = {
        title: citation.title || '',
        authors: citation.authors || '',
        year: citation.year || '',
        journal: citation.journal || '',
        pubmed_id: citation.pubmed_id || '',
        url: citation.url || '',
    };
    // Fallback: parse metadata from all stored citation formats
    if (!meta.title || !meta.authors) {
        const parsed = parseCitationFormats(citation.citationDict || null);
        if (parsed) {
            if (!meta.title) meta.title = parsed.title;
            if (!meta.authors) meta.authors = parsed.authors;
            if (!meta.year) meta.year = parsed.year;
            if (!meta.journal) meta.journal = parsed.journal;
        }
    }
    // Extract PMID from URL if not stored
    if (!meta.pubmed_id && meta.url) {
        const m = meta.url.match(/\/(\d+)\/?$/);
        if (m) meta.pubmed_id = m[1];
    }
    return meta;
}

function renderSummaryMarkdown(text) {
    const boldify = s => s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    const lines = text.split('\n');
    let html = '';
    let inList = false;
    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) {
            if (inList) { html += '</ul>'; inList = false; }
            continue;
        }
        if (line.startsWith('- ')) {
            if (!inList) { html += '<ul class="abstract-insight-list">'; inList = true; }
            html += `<li>${boldify(line.slice(2))}</li>`;
        } else {
            if (inList) { html += '</ul>'; inList = false; }
            // **Heading:** optional text — render heading on its own line, content below
            const headingMatch = line.match(/^\*\*(.+?:)\*\*\s*(.*)/s);
            if (headingMatch) {
                html += `<p class="ai-section-heading"><strong>${headingMatch[1]}</strong></p>`;
                if (headingMatch[2].trim()) {
                    html += `<p class="ai-section-body">${boldify(headingMatch[2])}</p>`;
                }
            } else {
                html += `<p>${boldify(line)}</p>`;
            }
        }
    }
    if (inList) html += '</ul>';
    return html;
}

function getOrCreateAbstractModal() {
    let modal = document.getElementById('abstractInsightModal');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'abstractInsightModal';
    modal.className = 'modal-overlay hidden';
    modal.innerHTML = `
        <div class="modal-content abstract-insight-modal-content">
            <div class="modal-header">
                <h3><span class="material-symbols-rounded notranslate" style="vertical-align: middle; margin-right: 6px; font-size: 1.1rem;">auto_awesome</span>AI Summary</h3>
                <button class="modal-close-btn" id="abstractInsightCloseBtn" type="button" title="Close" aria-label="Close">
                    <span class="material-symbols-rounded notranslate">close</span>
                </button>
            </div>
            <div class="modal-body abstract-insight-modal-body" id="abstractInsightBody"></div>
        </div>
    `;
    document.body.appendChild(modal);

    const close = () => {
        modal.classList.add('hidden');
        document.body.style.overflow = '';
    };

    modal.querySelector('#abstractInsightCloseBtn').addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.classList.contains('hidden')) close();
    });

    return modal;
}

function buildArticleHeader(meta) {
    if (!meta || (!meta.title && !meta.authors && !meta.pubmed_id && !meta.url)) return '';
    const titleLine = meta.title ? `<p class="ai-article-title">${escapeAttr(meta.title)}</p>` : '';
    const parts = [];
    if (meta.authors) parts.push(escapeAttr(meta.authors));
    if (meta.journal) parts.push(`<em>${escapeAttr(meta.journal)}</em>`);
    if (meta.year) parts.push(escapeAttr(meta.year));
    const metaLine = parts.length ? `<p class="ai-article-meta">${parts.join(' &nbsp;|&nbsp; ')}</p>` : '';
    const pmidLink = meta.pubmed_id
        ? `<p class="ai-article-links">PMID:&nbsp;${escapeAttr(meta.pubmed_id)}&nbsp;&nbsp;<a href="${escapeAttr(meta.url || `https://pubmed.ncbi.nlm.nih.gov/${meta.pubmed_id}/`)}" target="_blank" rel="noopener noreferrer">Full Article ↗</a></p>`
        : '';
    return `<div class="ai-article-header">${titleLine}${metaLine}${pmidLink}<hr class="ai-article-divider"></div>`;
}

// Cache of abstract summaries already fetched this session, keyed by pubmed_id
// (falls back to the abstract text itself if pubmed_id is unavailable). Mirrors
// the caching compare-button.js already does for the Summary Table — reopening
// the same citation's summary should be instant, not re-run the LLM.
const abstractSummaryCache = new Map();

async function showAbstractInsight(abstractText, meta) {
    const modal = getOrCreateAbstractModal();
    const body = modal.querySelector('#abstractInsightBody');
    const headerHTML = buildArticleHeader(meta);
    const cacheKey = meta?.pubmed_id || abstractText;

    const cachedSummary = abstractSummaryCache.get(cacheKey);
    if (cachedSummary !== undefined) {
        body.innerHTML = `${headerHTML}<div class="abstract-insight-summary">${renderSummaryMarkdown(cachedSummary)}</div>`;
        modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        return;
    }

    body.innerHTML = `${headerHTML}<div class="abstract-insight-loading"><div class="spinner"></div><span>Summarizing abstract...</span></div>`;
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';

    try {
        const token = localStorage.getItem('access_token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['X-Access-Token'] = token;

        const response = await fetch('/summarize-abstract', {
            method: 'POST',
            headers,
            body: JSON.stringify({ abstract: abstractText }),
        });

        if (!response.ok) {
            body.innerHTML = `${headerHTML}<p class="abstract-insight-error">Failed to generate summary. Please try again.</p>`;
            return;
        }

        const data = await response.json();
        if (data.error || data.detail) {
            body.innerHTML = `${headerHTML}<p class="abstract-insight-error">Failed to generate summary. Please try again.</p>`;
            return;
        }

        abstractSummaryCache.set(cacheKey, data.summary || '');
        body.innerHTML = `${headerHTML}<div class="abstract-insight-summary">${renderSummaryMarkdown(data.summary || '')}</div>`;
    } catch (err) {
        console.error('[AbstractInsight] Error:', err);
        body.innerHTML = `${headerHTML}<p class="abstract-insight-error">An error occurred. Please try again.</p>`;
    }
}

// Event delegation for abstract insight buttons — runs once at module load
document.addEventListener('click', (e) => {
    const btn = e.target.closest('.abstract-insight-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const abstract = btn.dataset.abstract;
    const meta = btn.dataset.meta ? JSON.parse(btn.dataset.meta) : {};
    if (abstract) showAbstractInsight(abstract, meta);
});

// Cache for user's citation format preference
let userCitationFormat = 'apa'; // Default to APA
let userCitationFormatLoaded = false; // Track if preferences have been loaded from database

// Cache citations by timestamp/searchId for format switching from toolbar
const citationsCache = new Map();

export function cacheCitations(timestampOrSearchId, citations) {
    if (timestampOrSearchId && citations && citations.length > 0) {
        citationsCache.set(String(timestampOrSearchId), citations);
    }
}

export function getCachedCitations(timestampOrSearchId) {
    return citationsCache.get(String(timestampOrSearchId));
}

export function formatCitationText(text) {
    if (!text) return '';
    
    // Decode HTML entities (e.g., &amp; → &, &lt; → <, etc.)
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    let decoded = textarea.value;
    
    // Convert markdown-style italics (*text*) to HTML <em> tags
    // Match *text* but not **text** (bold) - use negative lookahead/lookbehind
    decoded = decoded.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    return decoded;
}

// Function to get user's citation format preference
// Uses in-memory cache if available, otherwise fetches from database
export async function getUserCitationFormat(forceRefresh = false) {
    // Return cached value if already loaded and not forcing refresh
    if (userCitationFormatLoaded && !forceRefresh) {
        return userCitationFormat;
    }
    
    try {
        const response = await fetch('/api/auth/preferences', {
            headers: getAuthHeaders()
        });
        
        if (response.ok) {
            const data = await response.json();
            userCitationFormat = data.citation_format || 'apa';
            userCitationFormatLoaded = true; // Mark as loaded
            return userCitationFormat;
        }
    } catch (error) {
        console.error('[Citations] Failed to load citation format preference:', error);
        // If we have a cached value, use it even if fetch failed
        if (userCitationFormatLoaded) {
            return userCitationFormat;
        }
    }
    
    // Mark as loaded (with default) if not authenticated or error occurred
    userCitationFormatLoaded = true;
    return 'apa'; // Default to APA
}

export function generateReferencesHTML(citations, citationFormat = null) {
    if (!citations || citations.length === 0) {
        return '';
    }
    
    // Use provided format or cached user preference, default to 'apa'
    const format = citationFormat || userCitationFormat || 'apa';
    
    // Sort citations by citationNumber
    const sortedCitations = [...citations].sort((a, b) => (a.citationNumber || 0) - (b.citationNumber || 0));
    
    let referencesHTML = '<h3>References</h3><ol>';
    
    sortedCitations.forEach(citation => {
        // Use citationDict with user's preferred format, otherwise fallback to apa or citationText
        let citationText = '';
        if (citation.citationDict) {
            citationText = citation.citationDict[format] || citation.citationDict['apa'] || '';
        }
        
        // Fallback to citationText if citationDict doesn't have the format
        if (!citationText && citation.citationText) {
            citationText = citation.citationText;
        }
        
        const url = citation.url || '';
        
        // Format citation text: decode HTML entities and convert *text* to <em>text</em>
        citationText = formatCitationText(citationText);
        
        const hasAbstract = citation.abstract && typeof citation.abstract === 'string' && citation.abstract.trim().length > 0;
        const insightBtn = hasAbstract
            ? `<button type="button" class="abstract-insight-btn" data-tooltip="AI Abstract Summary" data-abstract="${escapeAttr(citation.abstract)}" data-meta="${escapeAttr(JSON.stringify(buildCitationMeta(citation)))}" aria-label="Get AI summary of this abstract"><span class="material-symbols-rounded notranslate">auto_awesome</span><span class="abstract-insight-label">Summarize</span></button>`
            : '';
        referencesHTML += `<li><a href="${url}" target="_blank" rel="noopener noreferrer">${citationText}</a>${insightBtn}</li>`;
    });
    
    referencesHTML += '</ol>';
    return referencesHTML;
}

export async function displayCitations(timestamp, citations) {
    if (!citations || citations.length === 0) {
        console.log('[Citations] No citations to display');
        return;
    }
    
    // Get user's citation format preference
    const citationFormat = await getUserCitationFormat();
    
    // Cache citations for format switching from toolbar
    cacheCitations(timestamp, citations);

    // Display citations in the References section
    const referencesSection = document.getElementById(`references-${timestamp}`);
    if (referencesSection) {
        const referencesHTML = generateReferencesHTML(citations, citationFormat);
        referencesSection.innerHTML = referencesHTML;
        referencesSection.classList.remove('hidden');
        referencesSection.classList.add('visible');
    }

    console.log(`[Citations] Displayed ${citations.length} citations with format: ${citationFormat}`);
}

export async function fetchAndDisplayCitations(timestamp, searchId, researchId) {
    if (!searchId || !researchId) {
        console.log('[Citations] SearchId or ResearchId not available, skipping citations fetch');
        return;
    }
    
    try {
        const token = localStorage.getItem('access_token');
        const headers = {};
        if (token) {
            headers['X-Access-Token'] = token;
        }
        
        let citationsUrl = `/get-citations?searchId=${encodeURIComponent(searchId)}&research_id=${encodeURIComponent(researchId)}`;
        
        const response = await fetch(citationsUrl, {
            headers: headers
        });
        
        if (!response.ok) {
            console.warn('[Citations] Failed to fetch citations:', response.status);
            return;
        }
        
        const data = await response.json();
        
        if (data.error) {
            console.warn('[Citations] Error fetching citations:', data.error);
            return;
        }
        
        const citations = data.citations || [];
        
        if (citations.length === 0) {
            console.log('[Citations] No citations found');
            return;
        }
        
        displayCitations(timestamp, citations);
        
    } catch (error) {
        console.error('[Citations] Error fetching citations:', error);
    }
}

// Export the cache state for use in preferences module
export function setUserCitationFormat(format) {
    userCitationFormat = format;
    userCitationFormatLoaded = true;
}

export function getUserCitationFormatCache() {
    return { format: userCitationFormat, loaded: userCitationFormatLoaded };
}

/** Update displayed references to a new format (used by toolbar citation format menu) */
export function updateReferencesFormat(timestampOrSearchId, format) {
    const citations = getCachedCitations(timestampOrSearchId);
    if (!citations || citations.length === 0) return;

    setUserCitationFormat(format);
    const referencesSection = document.getElementById(`references-${timestampOrSearchId}`);
    if (referencesSection) {
        const referencesHTML = generateReferencesHTML(citations, format);
        referencesSection.innerHTML = referencesHTML;
    }
}

const CITATION_FORMATS = [
    { id: 'ama', label: 'AMA' },
    { id: 'apa', label: 'APA' },
    { id: 'mla', label: 'MLA' },
    { id: 'nlm', label: 'NLM' }
];

/** Create citation format dropdown element for toolbar (between download and pubmed) */
export function createCitationFormatDropdown(timestampOrSearchId) {
    const wrapper = document.createElement('div');
    wrapper.className = 'userQuery-actions citation-format-actions';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'icon-btn';
    btn.setAttribute('data-tooltip', 'Citation Style (AMA, APA, NLM, MLA)');
    btn.setAttribute('data-short-label', 'Citation Style');
    btn.innerHTML = '<span class="material-symbols-rounded notranslate">custom_typography</span>';

    const menu = document.createElement('div');
    menu.className = 'userQuery-actions-menu';

    const currentFormat = (userCitationFormat || 'apa').toLowerCase();

    function setActiveItem(menuEl, format) {
        menuEl.querySelectorAll('.userQuery-menu-item').forEach((el) => {
            const isActive = (el.dataset.format || '').toLowerCase() === (format || '').toLowerCase();
            el.classList.toggle('citation-format-active', isActive);
            const check = el.querySelector('.citation-format-check');
            if (check) check.style.visibility = isActive ? 'visible' : 'hidden';
        });
    }

    CITATION_FORMATS.forEach(({ id, label }) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'userQuery-menu-item';
        item.dataset.format = id;
        const isActive = id === currentFormat;
        if (isActive) item.classList.add('citation-format-active');
        item.innerHTML = `<span class="citation-format-label">${label}</span><span class="material-symbols-rounded citation-format-check notranslate" style="visibility: ${isActive ? 'visible' : 'hidden'}">check</span>`;
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            menu.classList.remove('open');
            updateReferencesFormat(timestampOrSearchId, id);
            setActiveItem(menu, id);
        });
        menu.appendChild(item);
    });

    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        document.querySelectorAll('.userQuery-actions-menu.open').forEach((m) => {
            if (m !== menu) m.classList.remove('open');
        });
        menu.classList.toggle('open');
        setActiveItem(menu, userCitationFormat || 'apa');
    });

    wrapper.appendChild(btn);
    wrapper.appendChild(menu);
    return wrapper;
}
